/**
 * About.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Icon from '../components/Icon';
import { api } from '../lib/api';
import { useHashScroll } from '../lib/useHashScroll';
import { trackEvent } from '../lib/consent';

const PILLAR_ICONS = { wheel: 'wheel', script: 'language', lotus: 'lotus', shield: 'shield' };

export default function About() {
  const { t } = useTranslation();
  const [content, setContent] = useState(null);
  const [openFaq, setOpenFaq] = useState(-1);

  useEffect(() => {
    const controller = new AbortController();
    api.about(controller.signal).then(setContent).catch(() => {});
    return () => controller.abort();
  }, []);

  useHashScroll(Boolean(content));

  const about = content?.about;
  const sections = about?.sections || [];

  return (
    <>
      <header className="about-hero">
        <div className="shell shell--narrow stack">
          <p className="eyebrow eyebrow--on-dark">{t('about.eyebrow', 'About')}</p>
          <h1 className="display display--xl">{t('about.title', about?.title || 'About GOMPA')}</h1>
          <p className="lede">
            {t(
              'about.lede',
              about?.lede ||
                'GOMPA connects you to Tibetan Buddhist monasteries around the world. When you can’t be there in person, GOMPA carries your offering and your dedication directly to the monastery on your behalf.'
            )}
          </p>
        </div>
      </header>

      <section className="section">
        <div className="shell legal-layout">
          <nav className="legal-toc" aria-label={t('about.tocLabel', 'On this page')}>
            {sections.map((section) => (
              <a key={section.id} href={`#${section.id}`}>
                {t(`about.sections.${section.id}.heading`, section.heading)}
              </a>
            ))}
          </nav>

          <div className="prose">
            {sections.map((section) => (
              <section key={section.id}>
                <h2 id={section.id}>{t(`about.sections.${section.id}.heading`, section.heading)}</h2>
                {section.body?.map((paragraph, index) => (
                  <p key={index}>{t(`about.sections.${section.id}.p${index + 1}`, paragraph)}</p>
                ))}

                {section.groups?.map((group) => (
                  <div className="clause-group" key={group.title}>
                    <h3>{group.title}</h3>
                    <ul>
                      {group.items.map((item, i) => <li key={i}>{item}</li>)}
                    </ul>
                  </div>
                ))}

                {section.trailing?.map((paragraph, index) => (
                  <p key={`t${index}`}>{paragraph}</p>
                ))}
                {section.pending && (
                  <div className="pending-clause">
                    <b>
                      <Icon name="alert" size={12} style={{ verticalAlign: -2, marginRight: 5 }} />
                      {t('legal.pendingLabel', 'Not yet decided')}
                    </b>
                    <span>{section.pending}</span>
                  </div>
                )}
              </section>
            ))}
          </div>
        </div>
      </section>

      <section className="section section--paper" id="principles">
        <div className="shell">
          <div className="section-head section-head--center">
            <p className="eyebrow">{t('about.principlesEyebrow', 'Principles')}</p>
            <h2 className="display display--lg">{t('about.principlesTitle', 'What we stand by.')}</h2>
          </div>
          <div className="pillars">
            {(content?.pillars || []).map((pillar, index) => (
              <div className="card pillar" key={pillar.title}>
                <span className="pillar__plate">
                  <Icon name={PILLAR_ICONS[pillar.icon] || 'lotus'} size={20} />
                </span>
                <h3>{t(`landing.pillars.p${index + 1}Title`, pillar.title)}</h3>
                <p>{t(`landing.pillars.p${index + 1}Body`, pillar.body)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section" id="faq">
        <div className="shell shell--narrow">
          <div className="section-head">
            <p className="eyebrow">{t('about.faqEyebrow', 'Questions')}</p>
            <h2 className="display display--lg">{t('about.faqTitle', "Everything you'd want to know.")}</h2>
          </div>
          <div className="faq">
            {(content?.faq || []).map((item, index) => {
              const open = openFaq === index;
              return (
                <div className={`faq__item${open ? ' is-open' : ''}`} key={item.q}>
                  <h3>
                    <button
                      type="button"
                      className="faq__q"
                      aria-expanded={open}
                      onClick={() => {
                        trackEvent('faq_toggle', { question_index: index, opened: !open, location: 'about' });
                        setOpenFaq(open ? -1 : index);
                      }}
                    >
                      {t(`landing.faq.q${index + 1}`, item.q)}
                      <Icon name="chevronDown" size={18} />
                    </button>
                  </h3>
                  {/* Always mounted, collapsed via CSS (`.faq__a-wrap`) rather
                      than conditionally rendered — matches Landing's FAQ, and
                      keeps every answer in the rendered DOM so the FAQPage
                      structured data (server/src/lib/seo.js) describes content
                      that is actually present on the page, which Google
                      requires for the FAQ rich result. */}
                  <div className="faq__a-wrap">
                    <div className="faq__a-inner">
                      <p className="faq__a">{t(`landing.faq.a${index + 1}`, item.a)}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="row" style={{ justifyContent: 'center', marginTop: 'var(--space-3xl)' }}>
            <Link
              to="/explore"
              className="btn btn--primary btn--lg"
              onClick={() => trackEvent('cta_click', { cta_id: 'about_explore' })}
            >
              <Icon name="compass" size={18} /> {t('about.exploreBtn', 'Explore monasteries')}
            </Link>
            <Link
              to="/contact"
              className="btn btn--ghost btn--lg"
              onClick={() => trackEvent('cta_click', { cta_id: 'about_contact' })}
            >
              {t('about.contactBtn', 'Get in touch')}
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
